<template>
  <transition name="expand-table">
    <div
      v-if="showFilters"
      class="filter-panel-wrap"
      :class="isEmbedMode ? 'embeded' : ''"
      @click="onClickFilterSection"
    >
      <div v-if="loadingFilters">
        <b-loading-spinner />
      </div>
      <div v-else>
        <!-- MEDIA Section -->
        <div class="filter media-settings">
          <div class="filter-ttl">
            <span>MEDIA </span>
          </div>
          <div class="filter-group mediaFilters">
            <div class="filter-checkbox">
              <b-select-checkbox
                ref="ioDropdown"
                :options="filters.ioOptions || ioOptions"
                title=""
                header="Campaign"
                class="media-select"
                component-id="ioOptionDropdown"
                :show-all="filters.ioOptions.length > 1 || ioOptions.length > 1"
                :disabled="isAdGroupLoading || dataLoading"
                @selected-options="$emit('select-io', $event)"
                @dropdown-closed="handleIoDropdownClosed"
                @dropdown-opened="handleIoDropdownOpened"
              />
            </div>
            <div>
              <b-select-checkbox
                ref="adGroupDropdown"
                :options="filters.adGroupOptions || adGroupOptions"
                title=""
                header="Ad Group"
                :disabled="isAdGroupLoading || dataLoading"
                class="ad-group-select"
                :show-all="filters.adGroupOptions.length > 1 || adGroupOptions.length > 1"
                @selected-options="$emit('select-ad-groups', $event)"
                @dropdown-closed="handleAdGroupDropdownClosed"
                @dropdown-opened="handleAdGroupDropdownOpened"
              />
            </div>

            <div
              class="fp-text-link view-pop-fltr"
              :class="{ disabled: isAdGroupLoading || dataLoading }"
            >
              <p @click="$emit('toggle-advanced-filter-modal')">Advanced Filters</p>
              <span>
                {{ appliedFilterCount > 0 ? `(${appliedFilterCount} Applied)` : '' }}
                <font-awesome-icon
                  v-if="appliedFilterCount > 0 && !isAdGroupLoading"
                  :icon="['far', 'times']"
                  class="advance-filter-clear-icon"
                  @click="$emit('clear-advanced-filters')"
                />
              </span>
            </div>
          </div>
          <!-- added overlay property added to spinner for showing spinner on top of component -->
          <b-loading-spinner v-if="dataLoading" overlay />
        </div>

        <!-- ATTRIBUTION SETTINGS -->
        <div class="filter method-settings">
          <div class="filter-ttl"><span>ATTRIBUTION SETTINGS</span></div>
          <div :class="`filter-group ${isVCRCategory ? 'readonly-db' : ''}`">
            <b-radio-tab-group
              :initial-group="filters.totalOrUniqueRadioOptions || totalOrUniqueRadioOptions"
              class="tot-uniq"
              @update="$emit('update-total-unique', $event)"
            />
          </div>

          <!-- Methodology -->
          <div class="filter-ttl"><span>Methodology</span></div>
          <div :class="`filter-group ${isVCRCategory ? 'readonly-db' : ''}`">
            <b-select
              class="media-select"
              :options="filters.methodologyOptions"
              :selected="filters.methodology"
              @change="$emit('select-methodology', $event)"
            />

            <!-- Conversion Window -->
            <div class="conv-content">
              <div class="filter-ttl"><span>Conversion Window</span></div>
              <div class="conversion-opt-cont">
                <input
                  v-model="filters.conversionWindowDisplay"
                  type="text"
                  name="conversion-window"
                  class="flp-input input-conv"
                  @keypress="$emit('only-number', $event)"
                />
                <div class="opt-b2">
                  <b-checkbox
                    label="Days"
                    name="Days"
                    icon="circle"
                    :is-radio="true"
                    :checked="filters.conversionWindowUnit === 'DAYS'"
                    @change="$emit('update-conversion-unit', $event)"
                  />
                  <b-checkbox
                    label="Hours"
                    name="Hours"
                    icon="circle"
                    :is-radio="true"
                    :checked="filters.conversionWindowUnit === 'HOURS'"
                    @change="$emit('update-conversion-unit', $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bottom-padded">
          <k-button
            :size="3"
            label="APPLY"
            :class="{ disabled: isAdGroupLoading || dataLoading }"
            :disabled="isAdGroupLoading || dataLoading"
            @click="onApplyFilters"
          />
        </div>
        <font-awesome-icon
          :icon="['far', 'times']"
          class="close-filters"
          @click="$emit('close-filters')"
        />
      </div>
    </div>
  </transition>
</template>

<script>
import EventBus from '@/adready-vue/helpers/global/event-bus';
import { get } from 'vuex-pathify';
import BLoadingSpinner from './elements/b-loading-spinner.vue';

export default {
  name: 'LeftFilterPanel',
  components: {
    KButton: () => import(/* webpackChunkName: "k-button" */ '~/components/elements/k-button.vue'),
    BSelect: () => import(/* webpackChunkName: "b-select" */ '~/components/elements/b-select.vue'),
    BRadioTabGroup: () =>
      import(
        /* webpackChunkName: "b-radio-tab-group" */ '~/components/elements/b-radio-tab-group.vue'
      ),
    BCheckbox: () =>
      import(/* webpackChunkName: "b-checkbox" */ '~/components/elements/b-checkbox.vue'),
    BSelectCheckbox: () =>
      import(
        /* webpackChunkName: "b-select-checkbox" */ '~/components/elements/b-select-checkbox.vue'
      ),
    BLoadingSpinner,
  },
  props: {
    appliedFilterCount: {
      type: Number,
      required: true,
      default: 0,
    },
    filters: {
      type: Object,
      required: true,
      default: undefined,
    },
    showFilters: {
      type: Boolean,
      required: true,
      default: false,
    },
    loadingFilters: {
      type: Boolean,
      required: true,
      default: false,
    },
    isAdGroupLoading: {
      type: Boolean,
      required: true,
      default: false,
    },
    ioOptions: {
      type: Array,
      required: true,
      default: undefined,
    },
    adGroupOptions: {
      type: Array,
      required: true,
      default: undefined,
    },
    totalOrUniqueRadioOptions: {
      type: Array,
      required: true,
      default: undefined,
    },
    isVCRCategory: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      isIoDropdownOpen: false,
      isAdGroupDropdownOpen: false,
      pendingApply: false,
      awaitingIoProcess: false,
      awaitingAdGroupProcess: false,
    };
  },
  computed: {
    isEmbedMode: get('common/isEmbedMode'),
    dataLoading() {
      return this.pendingApply;
    },
  },
  mounted() {
    EventBus.$on('campaign-dropdown-processed', this.onCampaignProcessed);
    EventBus.$on('ad-group-dropdown-processed', this.onAdGroupProcessed);
  },
  beforeDestroy() {
    EventBus.$off('campaign-dropdown-processed', this.onCampaignProcessed);
    EventBus.$off('ad-group-dropdown-processed', this.onAdGroupProcessed);
  },
  methods: {
    onClickFilterSection() {
      this.$emit('click-filter-section');
    },
    handleIoDropdownOpened() {
      this.isIoDropdownOpen = true;
    },
    handleIoDropdownClosed(event) {
      this.$emit('campaign-dropdown-closed', event);
      this.isIoDropdownOpen = false;
    },
    handleAdGroupDropdownOpened() {
      this.isAdGroupDropdownOpen = true;
    },
    handleAdGroupDropdownClosed(event) {
      this.$emit('ad-group-dropdown-closed', event);
      this.isAdGroupDropdownOpen = false;
    },
    onCampaignProcessed() {
      this.awaitingIoProcess = false;
      this.checkApplyReady();
    },
    onAdGroupProcessed() {
      this.awaitingAdGroupProcess = false;
      this.checkApplyReady();
    },
    checkApplyReady() {
      if (this.pendingApply && !this.awaitingIoProcess && !this.awaitingAdGroupProcess) {
        this.pendingApply = false;
        this.$emit('apply-filters');
      }
    },
    onApplyFilters() {
      // Determine which dropdowns are open and mark awaiting flags
      this.pendingApply = true;
      if (this.isIoDropdownOpen && this.$refs.ioDropdown) {
        this.awaitingIoProcess = true;
        this.$refs.ioDropdown.closeDropdown();
      }
      if (this.isAdGroupDropdownOpen && this.$refs.adGroupDropdown) {
        this.awaitingAdGroupProcess = true;
        this.$refs.adGroupDropdown.closeDropdown();
      }
      // If neither was open just emit immediately
      if (!this.awaitingIoProcess && !this.awaitingAdGroupProcess) {
        this.pendingApply = false;
        this.$emit('apply-filters');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* stylelint-disable no-duplicate-selectors, declaration-block-no-duplicate-properties */
@media screen {
  * {
    box-sizing: border-box;
  }
  * {
    outline: 0;
  }

  .light-theme {
    .top-export-icon {
      color: #444;
    }
  }
  .top-export-icon {
    position: relative;
    display: inline-block;
    margin-top: 10px;
    margin-right: 30px;
    font-size: 21px;
    color: #ddd;
    vertical-align: top;
  }
  .top-export-icon.downloadIcon {
    margin: 12px 5px 0px 10px;
  }

  .top-export-icon:hover {
    color: var(--primarycolor);
  }
}

@media screen {
  * {
    box-sizing: border-box;
  }

  .wrap4icon.icon-cal {
    position: relative;
    display: inline-block;
  }
  * {
    outline: 0;
  }
  .intip4 {
    position: relative;
    top: 0;
  }
  .intip4[data-tool-tip]::after {
    position: absolute;
    bottom: 100%;
    left: 0;
    z-index: 99;
    display: block;
    width: 100px;
    padding: 10px 12px;
    margin-left: -50px;
    font-size: 12px;
    line-height: 1.2em;
    color: #5b5b5b;
    text-align: center;
    pointer-events: none;
    content: attr(data-tool-tip);
    background-color: #fdea85;
    border-radius: 3px;
    transition: ease 0.3s;
    transform: scale(0);
    transform-origin: bottom center;
  }
  .intip4[data-tool-tip]::after {
    width: 155px;
    margin-left: -70px;
    font-size: 12px;
    font-weight: 400;
    color: #222;
    pointer-events: none;
    background-color: rgba(245, 245, 250, 0.9);
  }
  .intip4[data-tool-tip]:hover::after {
    bottom: -145%;
    transform: scale(1);
  }
}

.filter-panel-wrap {
  align-items: center;
  margin-bottom: 0;
  color: #cad1d6;
  border-top: 1px solid #212429;
  transition: left 0.3s ease;
  .fp-text-link {
    font-size: 12px;
    color: #707e8a;
    display: inline-block;
    margin-top: 15px;
    display: flex;
    align-items: center;
    & p {
      text-decoration: underline;
      cursor: pointer;
    }
    & p:hover {
      color: var(--primarycolor) !important;
    }
    & span {
      text-decoration: none;
      margin-left: 8px;
      color: #cad1d6;
      .advance-filter-clear-icon:hover {
        color: var(--primarycolor) !important;
      }
    }
    & span:hover {
      color: #cad1d6;
    }
  }
}

.light-theme {
  .filter-panel-wrap {
    .filter {
      .filter-group {
        .flp-input {
          color: #212325;
          background-color: #fff;
          border: 1px solid #c5d2d8;
        }
      }
      ::v-deep .dropdown .dropdown-menu {
        li svg {
          color: #555 !important;
          &.check-off {
            color: transparent !important;
          }
        }
      }
    }
  }
}

::v-deep .filter-panel-wrap .filter.media-settings .dropdown .dropdown-menu {
  max-height: 300px !important;
  overflow: hidden !important;
  overflow-y: auto !important;
  li svg {
    font-size: 17px !important;
    box-shadow: inset 0px 0px 1px 1px #454546;
    color: #fff;
  }
}

.light-theme {
  .filter-panel-wrap {
    background-color: #fbfcfe;
    box-shadow: 10px 20px 20px rgb(63 66 77 / 16%);
    color: #212325;
    border-right: 1px solid #f2f6ff;
    border-top: unset;
    svg {
      color: #212325 !important;
    }
    .fp-text-link {
      p {
        color: #707e8a !important;
      }
      span {
        color: #212325 !important;
      }
    }
  }
}

.filter-checkbox {
  border: 1px;
}
.embeded {
  top: 20px !important;
  height: 97vh !important;
}
.filter-panel-wrap {
  position: fixed;
  top: 55px;
  bottom: 0;
  left: 0;
  z-index: 3;
  width: auto;
  min-width: 360px;
  height: 94vh;
  padding: 30px 80px 20px;
  background: #121215;
  background-color: rgb(16 16 19 / 93%);
  border-right: 1px solid #1d1f23;
  box-shadow: inset -10px 10px 20px rgb(0 0 0 / 30%);
  box-shadow: 10px 30px 30px rgb(0 0 0 / 50%);
  -webkit-transition: all 0.3s ease;
  -moz-transition: all 0.3s ease;
  -ms-transition: all 0.3s ease;
  -o-transition: all 0.3s ease;
  transition: all 0.3s ease;
  backdrop-filter: saturate(140%) blur(5px);
  overflow-y: scroll;
  overflow-x: hidden;
  span {
    margin-top: 2px;
  }
  svg {
    color: #cad1d6 !important;
  }
  .filter {
    ::v-deep .dropdown .dropdown-menu {
      overflow: unset !important;
      overflow-y: unset !important;
      li svg {
        font-size: inherit !important;
      }
      .intip4 {
        top: 2px !important;
        margin-left: 5px !important;
        &::after {
          bottom: 200% !important;
          width: 198px !important;
          margin-left: -73px !important;
          font-weight: 400 !important;
          text-align: left !important;
          letter-spacing: 0em !important;
        }
      }
    }
    .filter-ttl {
      margin-bottom: 10px;
      font-size: 13px;
      font-weight: 400;
      line-height: 1.1em;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .filter-group > *:not(:first-child) {
      margin-top: 10px;
    }

    .filter-group {
      margin-bottom: 30px;
      .conv-content {
        position: relative;
        margin-top: 30px;

        .conv-ttl {
          position: absolute;
          top: -80%;
          font-size: 14px;
          color: #707e8a;
        }
      }

      .opt-b1 {
        display: inline-block;
        margin-right: 6px;
      }
      .opt-b2 {
        display: inline-block;
        width: 5rem;
        margin-left: 5px;
        vertical-align: bottom;
        & .checkbox-btn {
          height: unset;
        }
        ::v-deep .checkbox-btn {
          span {
            margin: 0 30px !important;
            font-size: 12px !important;
            position: fixed;
          }
        }
      }

      .input-conv {
        width: 60px;
      }

      .flp-input {
        padding: 10px;
        color: #ddd;
        background-color: transparent;
        border: 1px solid rgba(170, 170, 170, 0.2);
        border-radius: 4px;
      }
    }
  }
  .media-select {
    width: 180px !important;
    min-width: 180px !important;
  }
  .ad-group-select {
    width: 180px !important;
    min-width: 180px !important;
  }
  .close-filters {
    position: absolute;
    top: 10px;
    right: 0;
    margin-right: 20px;
    cursor: pointer;
    &:hover {
      color: var(--primarycolor) !important;
    }
  }
}

// Top Area CSS - START

label {
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
input[type='checkbox'] {
  display: none;
}
input[type='checkbox'] + label:before {
  display: inline-block;
  font-family: 'FontAwesome';
}
input[type='checkbox'] + label:after {
  display: inline-block;
  font-family: 'FontAwesome';
}
input[type='checkbox'] + label:before {
  position: relative;
  top: -2px;
  left: 2px;
  z-index: 2;
  font-size: 14px;
  font-weight: 600;
  color: var(--primarycolor);
  letter-spacing: 10px;
  content: '\f00c';
  opacity: 0;
}
input[type='checkbox']:checked + label:before {
  font-weight: 600;
  letter-spacing: 10px;
  content: '\f00c';
  opacity: 1;
}
input[type='checkbox'] + label::after {
  position: absolute;
  left: 0px;
  font-size: 18px;
  font-weight: 500;
  color: rgba(150, 150, 150, 0.4);
  content: '\f1db';
}
input[type='checkbox']:checked + label::after {
  color: var(--primarycolor);
}
.icon-cal {
  position: relative;
  top: -6px;
  display: inline-flex;
  float: right;
  vertical-align: top;
  right: -18px;
  align-self: flex-end;
}
.light-theme {
  .icon-cal {
    right: -28px;
  }
  .sideopen {
    .icon-cal {
      right: 122px;
    }
  }
}
.sideopen {
  .icon-cal {
    right: 130px !important;
  }
}
::v-deep .icon-cal .wrap svg {
  position: absolute;
  top: 50%;
  right: 16px;
  z-index: 1;
  margin-top: -11px;
  pointer-events: none;
}
.intdark {
  color: #fff;
  background-color: #262933;
}
// Top Area CSS - END

// Last Area CSS - START

.nav-tabs {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 1;
  display: inline-block;
  margin: -33px 0px 0px 0;
}
.light-theme .left-part {
  background-color: #fff;
  color: #212325;
  box-shadow: 0px 14px 35px -12px rgba(0, 40, 100, 0.17);
  border: 1px solid rgb(165 173 178 / 22%);
  border-radius: 8px;
}

.left-part {
  position: relative;
  z-index: 2;
  float: left;
  width: 30%;
  min-width: 366px;
  min-height: 585px;
  padding: 30px 0px;
  color: #fff;
  background-color: #212429;
  border-radius: 0px;
}

// Download Icon CSS - START

svg:not(:root).svg-inline--fa {
  overflow: visible;
}
.svg-inline--fa {
  display: inline-block;
  height: 1em;
  overflow: visible;
  font-size: inherit;
  vertical-align: -0.125em;
}
.svg-inline--fa.fa-w-12 {
  width: 0.75em;
}

.fa-arrow-to-bottom:before {
  content: '\f33d';
}

// Download Icon CSS - END

// Export Table - START

svg:not(:root).svg-inline--fa {
  overflow: visible;
}
.svg-inline--fa {
  display: inline-block;
  height: 1em;
  overflow: visible;
  font-size: inherit;
  vertical-align: -0.125em;
}
.svg-inline--fa.fa-w-10 {
  width: 0.625em;
}

.fa-times:before {
  content: '\f00d';
}

.fa-times:before {
  content: '\f00d';
}
.top-last {
  margin-top: 0px;
}
::v-deep .dropdown {
  margin-right: 0 !important;
}

::v-deep .form-input-field {
  background-color: #262933;
  border: none;
  border-radius: 4px;
}
::v-deep .form-input-field.v-select .vs__dropdown-toggle .vs__actions .action-icons svg {
  color: inherit !important;
}
::v-deep .form-input-wrap .form-click .action-icons {
  padding: 0 11px 0 0 !important;
}
::v-deep .form-input-wrap .form-click {
  min-height: 40px;
}
::v-deep .form-input.form-date svg {
  left: 90%;
  z-index: 1;
}

.dboard-select {
  width: 12rem;
  min-width: 182px;
  margin-top: 4px;
  margin-right: 1.2rem;
  margin-left: 0;
  background-color: #474a56;
  border-radius: 4px;
}
::v-deep .icon-cal .form-input-field {
  width: 190px;
  margin: 4px 0 0 0;
  cursor: pointer;
}
::v-deep .k-btn {
  min-width: 75px;
  font-weight: 600;
  color: var(--buttontext) !important;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  background-color: var(--primarycolor);
  border-radius: 3px;
  appearance: none;
}
::v-deep .k-btn.btn-size-3 span.label {
  padding: 9px 22px;
  line-height: 20px;
}
::v-deep .k-btn:hover {
  color: #cad1d6 !important;
  background-color: #424242 !important;
}

.no-display {
  display: none !important;
}

.input-conv {
}
.bottom-padded {
  padding-bottom: 40px;
}
</style>
